import React from 'react';
import { ChevronUp, ChevronDown, Trash2, Plus } from 'lucide-react';
import type { LocaleCode } from '../Admin/I18nTripleFields';
import IconPicker from '../Admin/IconPicker';
import StringListEditor from './StringListEditor';
import { ITEM_FIELD_LABELS, ITEM_FIELD_MULTILINE, type ItemFieldKey } from './sectionFieldConfig';
import type { LandingSectionItem, LandingSectionItemLocale } from '../../i18n/localizeContent';

export type EditableItem = LandingSectionItem & {
  i18n: { pt: LandingSectionItemLocale; en: LandingSectionItemLocale; es: LandingSectionItemLocale };
};

interface SectionItemsEditorProps {
  active: LocaleCode;
  itemFields: ItemFieldKey[];
  items: EditableItem[];
  onChange: (next: EditableItem[]) => void;
}

const NUMBER_SUFFIX_OPTIONS = ['+', '%', '', '/7'];

function emptyItem(): EditableItem {
  return {
    icon: '',
    flag: '',
    number: null,
    numberSuffix: '',
    order: 0,
    ativo: true,
    i18n: {
      pt: { title: '', description: '', label: '', duration: '', features: [], popularVisas: [], requirements: [] },
      en: { title: '', description: '', label: '', duration: '', features: [], popularVisas: [], requirements: [] },
      es: { title: '', description: '', label: '', duration: '', features: [], popularVisas: [], requirements: [] }
    }
  };
}

const TEXT_FIELDS: ItemFieldKey[] = ['title', 'description', 'label', 'duration'];
const ARRAY_FIELDS: ItemFieldKey[] = ['features', 'popularVisas', 'requirements'];

const SectionItemsEditor: React.FC<SectionItemsEditorProps> = ({ active, itemFields, items, onChange }) => {
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...items, emptyItem()]);
  };

  const patchItem = (index: number, patch: Partial<EditableItem>) => {
    const next = [...items];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const patchLocale = (index: number, field: ItemFieldKey, value: string | string[]) => {
    const next = [...items];
    const item = next[index];
    next[index] = {
      ...item,
      i18n: {
        ...item.i18n,
        [active]: { ...item.i18n[active], [field]: value }
      }
    };
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const block = item.i18n[active] || {};
        return (
          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-500">Item {index + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30">
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => move(index, 1)} disabled={index === items.length - 1} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30">
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => remove(index)} className="p-1 text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {itemFields.includes('icon') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{ITEM_FIELD_LABELS.icon}</label>
                <IconPicker value={item.icon} onChange={(icon) => patchItem(index, { icon })} />
              </div>
            )}

            {itemFields.includes('flag') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{ITEM_FIELD_LABELS.flag}</label>
                <input
                  type="text"
                  value={item.flag || ''}
                  onChange={(e) => patchItem(index, { flag: e.target.value })}
                  className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                />
              </div>
            )}

            {itemFields.includes('number') && (
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ITEM_FIELD_LABELS.number}</label>
                  <input
                    type="number"
                    value={item.number ?? ''}
                    onChange={(e) => patchItem(index, { number: e.target.value === '' ? null : Number(e.target.value) })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sufixo</label>
                  <select
                    value={item.numberSuffix || ''}
                    onChange={(e) => patchItem(index, { numberSuffix: e.target.value })}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  >
                    {NUMBER_SUFFIX_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt || '(nenhum)'}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {itemFields.filter((f) => TEXT_FIELDS.includes(f)).map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{ITEM_FIELD_LABELS[field]}</label>
                {ITEM_FIELD_MULTILINE[field] ? (
                  <textarea
                    value={(block as any)[field] || ''}
                    onChange={(e) => patchLocale(index, field, e.target.value)}
                    rows={2}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  />
                ) : (
                  <input
                    type="text"
                    value={(block as any)[field] || ''}
                    onChange={(e) => patchLocale(index, field, e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  />
                )}
              </div>
            ))}

            {itemFields.filter((f) => ARRAY_FIELDS.includes(f)).map((field) => (
              <StringListEditor
                key={field}
                label={ITEM_FIELD_LABELS[field]}
                values={(block as any)[field] || []}
                onChange={(next) => patchLocale(index, field, next)}
              />
            ))}
          </div>
        );
      })}

      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 font-medium"
      >
        <Plus className="w-4 h-4" /> Adicionar item
      </button>
    </div>
  );
};

export default SectionItemsEditor;
export { emptyItem };
